import { QueryClient, useMutation, useQuery } from "@tanstack/react-query"
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout"
import { editCommunity, getCommunity } from "../../controllers/community/community"
import { useContext, useEffect, useMemo, useState } from "react"
import { UserContext } from "../../context/UserContext"
import { Button, Card, CardActions, CardContent, CardHeader, FormControl, FormControlLabel, FormLabel, Grid, List, ListItem, Radio, RadioGroup, TextField, Container, Box, Typography, Divider, Paper } from "@mui/material"
import { PostMedia } from "../../components/post/card/PostMedia"
import { useNavigate, useParams } from "react-router"
import { CategoryPicker } from "../../components/category.picker"
import { header_radio_set } from "../../util/util"
import { Delete, Image, Category, Lock, Public, People } from "@mui/icons-material"

export const EditCommunity = () => {
    const { userDetails } = useContext(UserContext)
    const [header_img, setHeaderImg] = useState('')
    const [media_ids, setMediaIds] = useState([])
    const [imageUrls, setImageUrls] = useState([])
    const [chooseHeader, setChooseHeader] = useState(true)
    const [title, setTitle] = useState('')
    const [category, setCategory] = useState('')
    const [privacy, setPrivacy] = useState(true)
    let navigate = useNavigate()
    const { id } = useParams()
    let token = userDetails?.token
    const { data:community, refetch } = useQuery({ queryKey: ['getSingleCommunity'], queryFn: () => getCommunity(token, id) })
    useEffect(() => {
        refetch()
    })

    let single_community = useMemo(() => community?.community ?? {}, [community?.community]) 
    
    useEffect(() => {
        single_community?.title && setTitle(single_community?.title)
        single_community?.category && setCategory(single_community?.category)
        setPrivacy(single_community?.privacy ?? true)
        setHeaderImg(single_community?.header_img ?? 'https://storage.googleapis.com/events.makegayfriends.com/event_images/uploads/community-1.jpg')
    }, [single_community])
    
    let field_set = [
        {id:"title", value:title, setValue:setTitle, label:"Title"},
    ]

    const queryClient = new QueryClient()
    const mutation = useMutation({
        mutationFn: editCommunity,
        onSuccess: (data) => {
        // Invalidate and refetch
        queryClient.invalidateQueries({ queryKey: ['editCommunity'] })
        navigate("/community/show/"+single_community?._id)
        },
    })
    
    const sendEditedCommunity = () => { 
        mutation.mutate({
          id:single_community?._id,
          body:{title, header_img, leader:single_community?.leader, category, privacy},
          token:userDetails?.token
        })
    }

    return (
        <MainLoggedInLayout>
            {single_community?.leader && single_community?.leader?.includes(userDetails?.id) ? (
                <Container maxWidth="lg" sx={{ py: 3 }}>
                    <Card sx={{ 
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                    }}>
                        <CardHeader 
                            title={
                                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                    Edit Community
                                </Typography>
                            }
                            subheader={
                                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                                    Update your community details and settings
                                </Typography>
                            }
                            action={
                                <Button 
                                    variant="outlined" 
                                    color="error" 
                                    startIcon={<Delete />}
                                    onClick={() => navigate("/community/delete/"+community?.community?._id)}
                                    sx={{ 
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Delete Community
                                </Button>
                            }
                            sx={{ pb: 1 }}
                        />
                        
                        <CardContent sx={{ p: 4 }}>
                            <Grid container spacing={4}>
                                {/* Basic Information */}
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 3, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                                            Basic Information
                                        </Typography>
                                        <TextField 
                                            fullWidth 
                                            value={title} 
                                            onChange={(e) => setTitle(e.target.value)} 
                                            label="Community Title" 
                                            variant="outlined"
                                        />
                                    </Paper>
                                </Grid>

                                {/* Privacy Settings */}
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 3, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                            <People sx={{ mr: 1, color: 'primary.main' }} />
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                Privacy Settings
                                            </Typography>
                                        </Box>
                                        
                                        <FormControl sx={{ width: '100%' }}>
                                            <RadioGroup
                                                value={privacy}
                                                onChange={(e) => setPrivacy(e.target.value)}
                                                sx={{ 
                                                    '& .MuiFormControlLabel-root': {
                                                        margin: 0,
                                                        padding: 2,
                                                        borderRadius: 2,
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(0,0,0,0.04)'
                                                        }
                                                    }
                                                }}
                                            >
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Box sx={{ 
                                                            border: privacy === 'true' ? '2px solid' : '1px solid',
                                                            borderColor: privacy === 'true' ? 'primary.main' : 'divider',
                                                            borderRadius: 2,
                                                            p: 2,
                                                            backgroundColor: privacy === 'true' ? 'primary.light' : 'transparent',
                                                            transition: 'all 0.2s'
                                                        }}>
                                                            <FormControlLabel 
                                                                value={true} 
                                                                control={<Radio />} 
                                                                label={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                        <Lock sx={{ mr: 1 }} />
                                                                        <Box>
                                                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                                                Private
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.secondary">
                                                                                Only members can see posts
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                }
                                                            />
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Box sx={{ 
                                                            border: privacy === 'false' ? '2px solid' : '1px solid',
                                                            borderColor: privacy === 'false' ? 'primary.main' : 'divider',
                                                            borderRadius: 2,
                                                            p: 2,
                                                            backgroundColor: privacy === 'false' ? 'primary.light' : 'transparent',
                                                            transition: 'all 0.2s'
                                                        }}>
                                                            <FormControlLabel 
                                                                value={false} 
                                                                control={<Radio />} 
                                                                label={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                        <Public sx={{ mr: 1 }} />
                                                                        <Box>
                                                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                                                Public
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.secondary">
                                                                                Anyone can see posts
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                }
                                                            />
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </RadioGroup>
                                        </FormControl>
                                    </Paper>
                                </Grid>

                                {/* Category */}
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 3, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Category sx={{ mr: 1, color: 'primary.main' }} />
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                Category
                                            </Typography>
                                        </Box>
                                        <CategoryPicker category={category} setCategory={setCategory} />
                                    </Paper>
                                </Grid>

                                {/* Community Image */}
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 3, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Image sx={{ mr: 1, color: 'primary.main' }} />
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                Community Image
                                            </Typography>
                                        </Box>
                                        
                                        <Box sx={{ mb: 3 }}>
                                            <img 
                                                src={header_img} 
                                                alt={single_community?.title} 
                                                style={{ 
                                                    width: '100%', 
                                                    maxHeight: '300px', 
                                                    objectFit: 'cover',
                                                    borderRadius: '8px'
                                                }} 
                                            />
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                            <Button 
                                                variant={chooseHeader ? "contained" : "outlined"}
                                                onClick={() => setChooseHeader(true)}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Choose from Gallery
                                            </Button>
                                            <Button 
                                                variant={!chooseHeader ? "contained" : "outlined"}
                                                onClick={() => setChooseHeader(false)}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Upload New Image
                                            </Button>
                                        </Box>

                                        {chooseHeader ? (
                                            <FormControl sx={{ width: '100%' }}>
                                                <FormLabel sx={{ mb: 2, fontWeight: 'bold' }}>Choose Community Image</FormLabel>
                                                <RadioGroup
                                                    value={header_img}
                                                    onChange={(e) => setHeaderImg(e.target.value)}
                                                >
                                                    <Grid container spacing={2}>
                                                        {header_radio_set.map((radio, index) => (
                                                            <Grid item xs={6} sm={4} md={3} key={index}>
                                                                <FormControlLabel 
                                                                    value={`https://storage.googleapis.com/events.makegayfriends.com/event_images/uploads/${radio}`} 
                                                                    control={<Radio />} 
                                                                    label={
                                                                        <img 
                                                                            alt="community pics" 
                                                                            src={`https://storage.googleapis.com/events.makegayfriends.com/event_images/uploads/${radio}`} 
                                                                            style={{ 
                                                                                width: '100%', 
                                                                                height: '80px', 
                                                                                objectFit: 'cover',
                                                                                borderRadius: '4px'
                                                                            }} 
                                                                        />
                                                                    } 
                                                                    sx={{ margin: 0 }}
                                                                />
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </RadioGroup>
                                            </FormControl>
                                        ) : (
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                                                    Upload Community Image
                                                </Typography>
                                                <PostMedia 
                                                    imageUrls={imageUrls} 
                                                    setImageUrls={setImageUrls} 
                                                    media_ids={media_ids} 
                                                    setMediaIds={setMediaIds} 
                                                    token={userDetails?.token} 
                                                />
                                            </Box>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                        
                        <Divider />
                        
                        <CardActions sx={{ p: 3 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Button 
                                        fullWidth 
                                        variant="outlined" 
                                        onClick={() => navigate("/community/show/"+single_community?._id)}
                                        sx={{ 
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Button 
                                        fullWidth 
                                        variant="contained" 
                                        onClick={sendEditedCommunity}
                                        sx={{ 
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Update Community
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardActions>
                    </Card>
                </Container>
            ) : (
                <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                    <Paper sx={{ p: 4, borderRadius: 3 }}>
                        <Typography variant="h5" color="error" gutterBottom>
                            Access Denied
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            You don't have permission to edit this community. Only community leaders can make changes.
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={() => navigate("/community/list")}
                            sx={{ 
                                textTransform: 'none',
                                fontWeight: 'bold',
                                borderRadius: 2
                            }}
                        >
                            Browse Communities
                        </Button>
                    </Paper>
                </Container>
            )}
        </MainLoggedInLayout>
    )
}